import { useMutation } from "@tanstack/react-query";
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { IRedirectMessageRef, TRedirectStatus, useToast } from "@/components";
import { useAuth } from "@/context/auth";
import { useI18n } from "@/context/i18n";

import { confirmAccountApi } from "./confirm-account.api";
import { IConfirmAccountHooks } from "./confirm-account.interface";
import { IConfirmAccountRequest } from "./confirm-account.types";

export const useConfirmAccount = (): IConfirmAccountHooks => {
  const navigate = useNavigate();
  const { handleAuthenticate } = useAuth();
  const { toast } = useToast();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const [confirmStatus, setConfirmStatus] =
    React.useState<TRedirectStatus>("loading");
  const token = searchParams.get("token");
  const alreadyCalledRef = React.useRef<boolean>(false);
  const redirectMessageRef = React.useRef<IRedirectMessageRef>(null);

  const confirmAccountMutation = useMutation({
    mutationFn: (data: IConfirmAccountRequest) =>
      confirmAccountApi.confirmAccount(data),
    onSuccess: (data) => {
      handleAuthenticate(
        {
          id: data.id,
          email: data.email,
          first_name: data.first_name,
          last_name: data.last_name,
          token: data.token,
          organization_root_id: data.organization_root_id,
        },
        "api"
      );
      setConfirmStatus("success");
      toast({
        title: t("generic.success"),
        description: t("generic.redirecting"),
      });
    },
    onError: (error: Error) => {
      setConfirmStatus("error");
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleConfirmAccount = React.useCallback(
    async (token: string) => {
      confirmAccountMutation.mutate({ token });
    },
    [confirmAccountMutation]
  );

  const confirmData = React.useMemo(() => {
    return {
      success: {
        message: t("auth.confirmAccountSuccess"),
        function: () => navigate("/dashboard"),
        type: "success",
        time: 5,
      },
      error: {
        message: t("auth.confirmAccountFailed"),
        function: () => navigate("/login"),
        type: "error",
        time: 10,
      },
      loading: {
        message: t("generic.loading"),
        function: () => {},
        type: "loading",
        time: 10,
      },
    };
  }, [t, navigate]);

  React.useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }

    if (!alreadyCalledRef.current) {
      alreadyCalledRef.current = true;
      handleConfirmAccount(token);
    }
  }, [token, navigate, handleConfirmAccount]);

  React.useEffect(() => {
    if (confirmStatus !== "loading") {
      redirectMessageRef.current?.handleMessage();
    }
  }, [confirmStatus]);

  return {
    handleConfirmAccount,
    confirmData,
    confirmStatus,
    redirectMessageRef,
  };
};
